import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** Per-device UI preferences. Local only; never sent to the server. */
interface Prefs {
  volume: number;
  muted: boolean;
  lowMotion: boolean;
  /** Users whose chat is hidden locally (user blocking). */
  blocked: string[];
  /** Whether the user already enabled playback once in this browser. */
  playbackConsent: boolean;
  /**
   * Keep the player running when this tab is hidden. ON by default (product decision); YouTube's developer
   * policies (III.I.9) restrict background playback, so review this before any API compliance review.
   */
  backgroundPlayback: boolean;
  setBackgroundPlayback(v: boolean): void;
  setVolume(v: number): void;
  setMuted(m: boolean): void;
  setLowMotion(v: boolean): void;
  toggleBlocked(userId: string): void;
  setPlaybackConsent(v: boolean): void;
}

const safeStorage = createJSONStorage(() => {
  try {
    const k = '__cs_probe';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (k: string) => mem.get(k) ?? null,
      setItem: (k: string, v: string) => void mem.set(k, v),
      removeItem: (k: string) => void mem.delete(k),
    };
  }
});

export const usePrefs = create<Prefs>()(
  persist(
    (set) => ({
      volume: 70,
      muted: false,
      lowMotion: false,
      blocked: [],
      playbackConsent: false,
      backgroundPlayback: true,
      setBackgroundPlayback: (backgroundPlayback) => set({ backgroundPlayback }),
      setVolume: (volume) => set({ volume: Math.max(0, Math.min(100, Math.round(volume))) }),
      setMuted: (muted) => set({ muted }),
      setLowMotion: (lowMotion) => set({ lowMotion }),
      toggleBlocked: (userId) =>
        set((s) => ({
          blocked: s.blocked.includes(userId)
            ? s.blocked.filter((b) => b !== userId)
            : [...s.blocked, userId],
        })),
      setPlaybackConsent: (playbackConsent) => set({ playbackConsent }),
    }),
    {
      name: 'crowdset.prefs',
      storage: safeStorage,
      partialize: (s) => ({
        volume: s.volume,
        muted: s.muted,
        lowMotion: s.lowMotion,
        blocked: s.blocked,
        playbackConsent: s.playbackConsent,
        backgroundPlayback: s.backgroundPlayback,
      }),
    },
  ),
);
